import { createAction } from '@reduxjs/toolkit';
import { SnitcherEventNames } from 'core/analytics/entities';
import { notAvailable } from 'core/common/constants';
import { normalizeError } from 'core/common/errors';
import {
  ATTRS_ANALYTICS_FLOW_NAME,
  ATTRS_ANALYTICS_FUNNEL_LOCALE,
  ATTRS_ANALYTICS_FUNNEL_NAME,
  SpanName,
} from 'core/common/observability/entities';
import { AppThunk } from 'core/common/store/types';
import { getQuizData } from 'core/funnel/store/selectors';
import { getUserInformation, getUserLocation } from 'core/user/store/selectors';

export const createAskNebulaAccountPending = createAction('auth/CREATE_ASKNEBULA_ACCOUNT/pending');
export const createAskNebulaAccountSucceed = createAction<string>(
  'auth/CREATE_ASKNEBULA_ACCOUNT/succeed',
);
export const createAskNebulaAccountFailed = createAction('auth/CREATE_ASKNEBULA_ACCOUNT/failed');

export const createAskNebulaAccountRequest =
  (): AppThunk => async (dispatch, getState, context) => {
    const {
      loggingService: logger,
      observabilitySystem,
      analyticsService,
      errorMonitoringService,
      userService,
      utmTagsService,
      funnelSettingsService,
      appEnvironment,
      snitcherService,
    } = context;

    const funnelName = funnelSettingsService.getFunnelName();
    const flowName = funnelSettingsService.getFlowName();

    const spanAttributes = {
      [ATTRS_ANALYTICS_FUNNEL_NAME]: funnelName,
      [ATTRS_ANALYTICS_FLOW_NAME]: flowName,
      [ATTRS_ANALYTICS_FUNNEL_LOCALE]: funnelSettingsService.getFunnelLocale(),
    };

    const span = observabilitySystem.startSpan(SpanName.CREATE_ASKNEBULA_ACCOUNT);
    span.setAttributes(spanAttributes);

    const quizData = getQuizData(getState());
    const userLocation = getUserLocation(getState());
    const userInformation = getUserInformation(getState());

    const userAgent = appEnvironment.getUserAgent();
    const deviceId = analyticsService.getDeviceId();
    const sessionId = analyticsService.getSessionId();

    const splitId = appEnvironment.getSplitId();
    const {
      fbp = '',
      fbc = '',
      gclid = '',
      quizz = '',
      utm_ad = '',
      utm_adset = '',
      utm_ad_id = '',
      utm_source = '',
      utm_adset_id = '',
      utm_campaign = '',
      utm_campaign_id = '',
    } = utmTagsService.getUtmTags();

    try {
      dispatch(createAskNebulaAccountPending());

      const result = await userService.createAskNebulaUser({
        ...quizData,
        split_id: splitId,
        do_not_sell_data: false,
        country: userLocation.country,
        birth_day: userInformation.date || '1990-01-01',
        birth_place: userInformation.location || 'Greenwich, Greater London, United Kingdom',
        longitude: userInformation.place?.[0] || 0.05,
        latitude: userInformation.place?.[1] || 51.45,
        acquisition_source: 'appnebula',
        media_source: utm_source,
        ad: utm_ad,
        ad_id: utm_ad_id,
        adset: utm_adset,
        adset_id: utm_adset_id,
        campaign: utm_campaign,
        campaign_id: utm_campaign_id,
        quizz,
        fbp,
        fbc,
        gclid,
        quiz: funnelName,
        flow_name: flowName,
        deviceId,
        sessionId,
        user_agent: userAgent,
      });

      dispatch(createAskNebulaAccountSucceed(result.authenticationToken));
      analyticsService.updateUserProperties({ user_id: result.accountId });

      await snitcherService.trackEvent(SnitcherEventNames.WEB_INSTALL, {
        email: quizData.email,
        funnel: 'appnebula',
      });

      span.succeed();
    } catch (e) {
      const err = normalizeError(e);
      logger.error(err);

      dispatch(createAskNebulaAccountFailed());

      errorMonitoringService.captureError(err, {
        tags: {
          get_params: location.search || notAvailable,
          error_message: err.message,
          error_reason: 'create_asknebula_account',
        },
      });

      span.fail(err);
    }
  };
